import React, { useState } from "react";
import Button from "@amzn/meridian/esm/components/button";
import closeSmallTokens from "@amzn/meridian-tokens/base/icon/close-small";
import Column from "@amzn/meridian/esm/components/column";
import Icon from "@amzn/meridian/esm/components/icon";
import Modal from "@amzn/meridian/esm/components/modal";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Textarea from "@amzn/meridian/esm/components/textarea";
import Theme from "@amzn/meridian/esm/components/theme/theme";

import ProductFeedbackRating from "./ProductFeedbackRating";
import LogManager from "../../utils/LogManager";
import aimsTheme from "../../../meridian-theme-aims.json";
import FeedbackPopoverTop from "../../../images/feedback_popover_top.svg";
import feedbackNotSubmitted from "../../../images/feedback_not_submitted.svg";
import feedbackSubmitted from "../../../images/feedback_submitted.svg";
import { FEEDBACK_MESSAGES, FEEDBACK_MODAL_HEADER, FEEDBACK_MODAL_STAGE, FEEDBACK_TYPE } from '../constants';

/**
 * ProductFeedback component for collecting user feedback about the product.
 * Handles user feedback submission via a modal interface and API interaction.
 * 
 * @param {Object} props
 * @param {boolean} props.isModalOpen - Controls the visibility of the feedback modal
 * @param {Function} props.setIsModalOpen - Function to update the modal's open state
 * @returns {JSX.Element} Product feedback UI Modal
 */
const ProductFeedback = ({ isModalOpen, setIsModalOpen }) => {
    const [stage, setStage] = useState(FEEDBACK_MODAL_STAGE.RATING);
    const [comment, setComment] = useState(undefined);
    const [rating, setRating] = useState(undefined);

    const onCloseModal = () => {
        setIsModalOpen(false);
        setComment(undefined);
        setRating(undefined);
        setStage(FEEDBACK_MODAL_STAGE.RATING);
    };

    const submitFeedback = async () => {
        LogManager.info("Calling /feedback API to submit product feedback");
        try {
            const response = await fetch('/feedback', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    type: FEEDBACK_TYPE.PRODUCT,
                    comment: comment,
                    rating: rating,
                    timestamp: Math.floor(Date.now() / 1000),
                })
            });
            const responseJson = await response.json();
            LogManager.info("The response of /feedback is:", responseJson);

            //TODO error handling for statusCode
            const statusCode = responseJson.StatusCode;
        } catch (error) {
            LogManager.error('Error submitting feedback:', error);
        }
    };

    const handleSubmit = async () => {
        setStage(FEEDBACK_MODAL_STAGE.SUBMITTED);
        await submitFeedback();
    };

    return (
        <>
            {isModalOpen &&
                <Theme palette={aimsTheme}>
                    <Modal
                        open={true}
                        width="292px"
                        bodySpacingInset="none"
                        fullscreenBreakpoint="0"
                    >
                        <Column heights={["66px","113px","56px"]} height="235px" spacing="none">
                            {stage === FEEDBACK_MODAL_STAGE.RATING &&
                                <>
                                    <div                     
                                        style={{
                                            background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                            width: "100%",
                                        }}
                                    >
                                        <Row widths={["10%", "70%","20%"]} alignmentVertical="top">
                                            <div/>
                                            <Column spacing="none">
                                                <Row height="24px"/>
                                                <Text type="h50">
                                                    {FEEDBACK_MODAL_HEADER.PRODUCT}
                                                </Text>
                                            </Column>
                                            <Button type="icon" onClick={onCloseModal}>
                                                <Icon tokens={closeSmallTokens}/>
                                            </Button>
                                        </Row>
                                    </div>
                                    <ProductFeedbackRating rating={rating} setRating={setRating}/>
                                    <Row alignmentVertical="top" spacing="none">
                                        <Column width="24px"/>
                                        <Button onClick={() => setStage(FEEDBACK_MODAL_STAGE.COMMENT)} size="small" type="link" minWidth="88px">
                                            Tell us why
                                        </Button>
                                        <Column width="68px"/>
                                        <Button type="primary" size="small" minWidth="88px" onClick={handleSubmit}>
                                            Submit
                                        </Button>
                                        <Column width="24px"/>
                                    </Row>
                                </>
                            }
                            {stage === FEEDBACK_MODAL_STAGE.COMMENT &&
                                <>
                                    <div                     
                                        style={{
                                            background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                            width: "100%",
                                        }}
                                    >
                                        <Row widths={["10%", "70%","20%"]} alignmentVertical="top">
                                            <div/>
                                            <Column spacing="none">
                                                <Row height="24px"/>
                                                <Text type="h50">
                                                    Tell us why
                                                </Text>
                                            </Column>
                                            <Button type="icon" onClick={onCloseModal}>
                                                <Icon tokens={closeSmallTokens}/>
                                            </Button>
                                        </Row>
                                    </div>
                                    <Row alignmentHorizontal="center">
                                        <Textarea
                                            value={comment}
                                            onChange={setComment}
                                            placeholder="Enter comment"
                                            error={false}
                                            width="244px"
                                            rows={3}
                                            size="small"
                                            resize="none"
                                        />
                                    </Row>
                                    <Row alignmentVertical="top" spacing="none">
                                        <Column width="24px"/>
                                        <Button onClick={() => setStage(FEEDBACK_MODAL_STAGE.RATING)} size="small" type="link" minWidth="88px">
                                            Back
                                        </Button>
                                        <Column width="68px"/>
                                        <Button type="primary" size="small" minWidth="88px" onClick={handleSubmit}>
                                            Submit
                                        </Button>
                                        <Column width="24px"/>
                                    </Row>
                                </>
                            }
                            {stage === FEEDBACK_MODAL_STAGE.SUBMITTED &&
                                <>
                                    <div                     
                                        style={{
                                            background: `url(${FeedbackPopoverTop}) no-repeat center/cover`,
                                            width: "100%",
                                        }}
                                    >
                                        <Row widths={["80%","20%"]} alignmentVertical="bottom">
                                            <div/>
                                            <Button type="icon" onClick={onCloseModal}>
                                                <Icon tokens={closeSmallTokens}/>
                                            </Button>
                                        </Row>
                                    </div>
                                    <Column alignmentHorizontal="center" alignmentVertical="top">
                                        {comment || rating ? 
                                        <>
                                            <img
                                                src={feedbackSubmitted}
                                                style={{width: '90px', height: 'auto', maxWidth: '100%'}}
                                            />
                                            <Text type="h50">
                                                {FEEDBACK_MESSAGES.SUBMITTED}
                                            </Text>
                                        </>
                                        :
                                        <>
                                            <img
                                                src={feedbackNotSubmitted}
                                                style={{width: '42px', height: 'auto', maxWidth: '100%'}}
                                            />
                                            <Text type="h50">
                                                {FEEDBACK_MESSAGES.NOT_SUBMITTED}
                                            </Text>
                                        </>
                                        }
                                    </Column>
                                    <Row alignmentVertical="top" spacing="none">
                                        <Column width="180px"/>
                                        <Button type="primary" size="small" minWidth="90px" onClick={onCloseModal}>Close</Button>
                                    </Row>
                                </>
                            }
                        </Column>
                    </Modal>
                </Theme>
            }
        </>
    );
};

export default ProductFeedback;